/**
 * The admin console, over the wire.
 *
 * Accounts live in Postgres now, so the console asks the server rather than
 * reading `localStorage` — which is why every button in it had quietly stopped
 * working. It could only ever see accounts that had signed in on this machine,
 * and edits it made were overwritten by the next load.
 *
 * Whether these are *allowed* is decided server-side from the verified token.
 * The client's own `isAdmin` only decides whether to draw the button.
 */

import type { CargoItem, PlayerRecord } from '@interspacial/shared';

import { NotSignedInError, OfflineError, RefusedError } from './account';

const BASE = '/interspacial/api';

/** A player as the console lists them. Mirrors `AdminPlayer` on the server. */
export interface AdminPlayer {
  uuid: string;
  displayId: string;
  username: string;
  banked: number;
  onboarded: boolean;
  tutorial: string | null;
  systemId: string | null;
  stationId: string | null;
  lastSeen: string;
  hasShip: boolean;
}

/**
 * 403 is a refusal here, not a failure.
 *
 * "You are not an administrator" is a sentence to put on screen. The ordinary
 * action path treats 409 that way and lets 403 fall through to a bare Error,
 * which would reach the console as "/admin/give failed: 403" — true, and no use
 * to anybody.
 */
async function call(token: string, path: string, body?: unknown): Promise<unknown> {
  let response: Response;

  try {
    response = await fetch(`${BASE}${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (error) {
    throw new OfflineError(error);
  }

  if (response.status === 401) throw new NotSignedInError();

  if (response.status === 403 || response.status === 409) {
    const said = (await response.json().catch(() => ({}))) as { error?: string };
    throw new RefusedError(said.error ?? 'Not allowed.');
  }

  if (!response.ok) throw new Error(`${path} failed: ${response.status}`);
  return response.json();
}

export async function fetchPlayers(token: string): Promise<AdminPlayer[]> {
  const body = (await call(token, '/admin/players')) as { players: AdminPlayer[] };
  return body.players;
}

/** Every one of these answers with the target's whole record, freshly read. */
async function edit(token: string, path: string, body: unknown): Promise<PlayerRecord> {
  const answer = (await call(token, path, body)) as { state: PlayerRecord };
  return answer.state;
}

/** One player's whole record — the hold, the purse, the hull. */
export function readPlayer(token: string, uuid: string): Promise<PlayerRecord> {
  return edit(token, '/admin/player', { uuid });
}

export function givePlayer(
  token: string,
  uuid: string,
  item: CargoItem,
): Promise<PlayerRecord> {
  return edit(token, '/admin/give', { uuid, item });
}

export function setStack(
  token: string,
  uuid: string,
  itemId: string,
  count: number,
): Promise<PlayerRecord> {
  return edit(token, '/admin/stack', { uuid, itemId, count });
}

export function clearHold(token: string, uuid: string): Promise<PlayerRecord> {
  return edit(token, '/admin/hold/clear', { uuid });
}

export function setWallet(
  token: string,
  uuid: string,
  wallet: { carried?: number; banked?: number },
): Promise<PlayerRecord> {
  return edit(token, '/admin/wallet', { uuid, ...wallet });
}

/**
 * Move a player to a system, docking them if there is anywhere to dock.
 *
 * The answer to two test accounts that started in different systems — otherwise
 * a long flight, or an overdrive core, away from being in the same place. The
 * station is optional: a system with none is a real destination, and it is
 * exactly where somebody worth joining tends to be.
 */
export function placePlayer(
  token: string,
  uuid: string,
  where: { systemId: string; stationId?: string | null; padId?: string },
): Promise<PlayerRecord> {
  return edit(token, '/admin/place', { uuid, ...where });
}

export async function resetPlayer(token: string, uuid: string): Promise<void> {
  await call(token, '/admin/reset', { uuid });
}
